/**
 * Logic for the customer-facing "missing details" request page.
 *
 * Deliberately DOM-free so it can be unit-tested with `node --test`; the wiring lives in
 * assets/missing-details-page-ui.js. Same UMD shim as assets/revision-page.js - a plain
 * script in the theme, a module under node, no build step.
 *
 * The API (TellMyTale-Klaviyo-Integration-API) returns items shaped like:
 *   { id, productTitle, missingFields: [{ key, label, options? }] }
 * where `key` is one of FIELD_SPECS below (`options` only on two-person gender fields).
 * Submission is a multipart POST whose field names are `<param>_<itemId>` - the one rule
 * this whole file exists to enforce is that a card only ever contributes the fields it
 * actually asked for, never a stray name/age for a photo-only item or vice versa.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.MissingDetailsPage = api;
})(typeof self !== 'undefined' ? self : this, function () {
  var FIELD_KEYS = {
    PHOTO: '_uploaded_image_url',
    NAME: '_name',
    AGE: '_age',
    GENDER: '_gender'
  };

  // Every key the API can ask for, in the order the form sends them, with the kind of input
  // it needs and its multipart name (`<param>_<itemId>`). _person1.._gender2 are two-person
  // ("multiface") books - mirrors MULTIFACE_FIELD_PARAMS in the API's
  // utils/missingDetailsFields.js.
  var FIELD_SPECS = [
    { key: '_uploaded_image_url', kind: 'photo', param: 'photo' },
    { key: '_name', kind: 'text', param: 'name' },
    { key: '_age', kind: 'age', param: 'age' },
    { key: '_gender', kind: 'choice', param: 'gender' },
    { key: '_person1', kind: 'photo', param: 'photo1' },
    { key: '_name1', kind: 'text', param: 'name1' },
    { key: '_age1', kind: 'age', param: 'age1' },
    { key: '_gender1', kind: 'choice', param: 'gender1' },
    { key: '_person2', kind: 'photo', param: 'photo2' },
    { key: '_name2', kind: 'text', param: 'name2' },
    { key: '_age2', kind: 'age', param: 'age2' },
    { key: '_gender2', kind: 'choice', param: 'gender2' }
  ];

  // A choice field without its own options is the single-child gender question.
  var DEFAULT_CHOICES = ['boy', 'girl'];

  function specFor(key) {
    for (var i = 0; i < FIELD_SPECS.length; i++) {
      if (FIELD_SPECS[i].key === key) return FIELD_SPECS[i];
    }
    return null;
  }

  function findField(item, key) {
    var fields = (item && item.missingFields) || [];
    for (var i = 0; i < fields.length; i++) {
      if (fields[i] && fields[i].key === key) return fields[i];
    }
    return null;
  }

  /** True when `item.missingFields` lists `key`. */
  function itemNeeds(item, key) {
    return Boolean(findField(item, key));
  }

  /** The multipart field name the API's submit endpoint expects for this key + item id. */
  function fieldNameFor(key, itemId) {
    var spec = specFor(key);
    return spec ? spec.param + '_' + itemId : null;
  }

  function normalizeText(value) {
    return typeof value === 'string' ? value.trim() : value;
  }

  function isFilledText(value) {
    return typeof value === 'string' && value.trim().length > 0;
  }

  function isFilledAge(value) {
    if (value === null || value === undefined || value === '') return false;
    var n = Number(value);
    return Number.isFinite(n) && n > 0;
  }

  /** The values a choice field accepts: its own options, else boy/girl. */
  function choiceValues(field) {
    var options = field && Array.isArray(field.options) ? field.options : [];
    var values = options
      .map(function (o) {
        return o && o.value;
      })
      .filter(Boolean);
    return values.length ? values : DEFAULT_CHOICES;
  }

  /** True when `values` (an item's { <param>: value }) has this field filled in. */
  function isFieldFilled(field, values) {
    values = values || {};
    var spec = specFor(field && field.key);
    if (!spec) return true; // an unrecognised key never blocks submit - nothing is rendered for it anyway
    var value = values[spec.param];
    if (spec.kind === 'photo') return Boolean(value);
    if (spec.kind === 'text') return isFilledText(value);
    if (spec.kind === 'age') return isFilledAge(value);
    return choiceValues(field).indexOf(value) !== -1;
  }

  /**
   * Every multipart field the submit request should carry, built strictly from each item's
   * own `missingFields` - never from what happens to be sitting in `formValues`. A field
   * with no value yet is left out entirely rather than sent empty - the caller is expected
   * to use canSubmit() first to decide whether it is even worth calling this.
   *
   * `items`: [{ id, missingFields: [{ key, label, options? }] }]
   * `formValues`: { [itemId]: { [param]: value } }   e.g. { photo, name } or { photo1, name2 }
   * Returns: [{ name: '<param>_<id>', value }]
   */
  function buildSubmitPayloadFields(items, formValues) {
    formValues = formValues || {};
    var fields = [];

    (items || []).forEach(function (item) {
      var values = formValues[item.id] || {};
      FIELD_SPECS.forEach(function (spec) {
        var field = findField(item, spec.key);
        if (!field || !isFieldFilled(field, values)) return;
        var value = values[spec.param];
        if (spec.kind === 'text') value = normalizeText(value);
        else if (spec.kind === 'age') value = String(value).trim();
        fields.push({ name: spec.param + '_' + item.id, value: value });
      });
    });

    return fields;
  }

  /**
   * True only once every missing field of every item has a value. Mirrors validateSubmission
   * on the server but runs client-side, so the Submit button can simply stay disabled.
   */
  function canSubmit(items, formValues) {
    formValues = formValues || {};
    if (!Array.isArray(items)) return false;

    return items.every(function (item) {
      var fields = (item && item.missingFields) || [];
      var values = formValues[item.id] || {};
      return fields.every(function (f) {
        return f && isFieldFilled(f, values);
      });
    });
  }

  return {
    FIELD_KEYS: FIELD_KEYS,
    FIELD_SPECS: FIELD_SPECS,
    specFor: specFor,
    choiceValues: choiceValues,
    itemNeeds: itemNeeds,
    fieldNameFor: fieldNameFor,
    buildSubmitPayloadFields: buildSubmitPayloadFields,
    canSubmit: canSubmit
  };
});
